import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import MuiButton from "@material-ui/core/Button";
import CustomTabs from "components/CustomTabs/CustomTabs.js";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import ReactTable from "components/ReactTable/ReactTable";
import {
  MessageOutlined,
  RemoveRedEye,
  Close,
  Add,
} from "@material-ui/icons";

const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

const styles = {
  searchWrapper: {
    marginTop: "10px",
  },
  dialogTitleRoot: {
    padding: "0",
  },
  dialogHeader: {
    backgroundColor: "#6B9D94",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 20px",
  },
  dialogHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  dialogContent: {
    padding: "24px !important",
  },
  dialogActions: {
    padding: "16px 24px 24px 24px",
    display: "flex",
    justifyContent: "flex-start",
    gap: "10px",
    flexWrap: "wrap",
  },
  tableToolbar: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginBottom: "12px",
    gap: "10px",
    flexWrap: "wrap",
  },
  systemButton: {
    backgroundColor: "#6B9D94",
    color: "#fff",
    fontWeight: 600,
  },
  warningButton: {
    backgroundColor: "#F2B500",
    color: "#000",
    fontWeight: 600,
  },
  deleteXIcon: {
    color: "#d32f2f",
    cursor: "pointer",
    fontWeight: 700,
  },
  inputSpacing: {
    marginBottom: "16px",
  },
};

const useStyles = makeStyles(styles);

const EMPTY_OVERALL = {
  total_books: 0,
  avg_completion: 0,
  avg_minutes_per_day: 0,
  avg_wpm: 0,
  avg_comprehension: 0,
  avg_fluency: 0,
};

const MAX_GET_RETRIES = 5;
const GET_RETRY_DELAY_MS = 500;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeSchoolIdValue = (value) =>
  String(value || "").trim().toLowerCase();

const retryAsync = async (
  fn,
  maxAttempts = MAX_GET_RETRIES,
  delayMs = GET_RETRY_DELAY_MS
) => {
  let lastError = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxAttempts) {
        await sleep(delayMs);
      }
    }
  }

  throw lastError;
};

const retryAxiosGet = async (
  url,
  config = {},
  maxAttempts = MAX_GET_RETRIES,
  delayMs = GET_RETRY_DELAY_MS
) => {
  return retryAsync(() => axios.get(url, config), maxAttempts, delayMs);
};

const retryFetchJson = async (
  url,
  options = {},
  maxAttempts = MAX_GET_RETRIES,
  delayMs = GET_RETRY_DELAY_MS
) => {
  return retryAsync(async () => {
    const response = await fetch(url, {
      ...options,
      method: "GET",
    });

    let data = null;
    try {
      data = await response.json();
    } catch (e) {
      data = null;
    }

    if (!response.ok) {
      const error = new Error(
        data?.message || `Request failed with status ${response.status}`
      );
      error.response = {
        status: response.status,
        data,
      };
      throw error;
    }

    return data;
  }, maxAttempts, delayMs);
};



const flattenSchoolStudentsResponse = (payload) => {
  const schools = Array.isArray(payload?.data) ? payload.data : [];
  const arr = [];

  for (let i = 0; i < schools.length; i++) {
    const school = schools[i];
    const teachers = Array.isArray(school?.teachers) ? school.teachers : [];

    for (let j = 0; j < teachers.length; j++) {
      const teacher = teachers[j];
      const students = Array.isArray(teacher?.students) ? teacher.students : [];

      for (let k = 0; k < students.length; k++) {
        const student = students[k];

        arr.push({
          student_id: student?.student_id ? Number(student.student_id) : null,
          first_name: student?.first_name || "",
          last_name: student?.last_name || "",
          email: student?.email || "",
          school_id: student?.school_id || school?.school_id || "",
          teacher_name:
            teacher?.teacher_full_name ||
            `${teacher?.teacher_first_name || ""} ${
              teacher?.teacher_last_name || ""
            }`.trim() ||
            "Unassigned",
          username: student?.username || "",
          password: student?.password || "",
        });
      }
    }
  }

  return arr;
};

const flattenSchoolTeachersResponse = (payload) => {
  const schools = Array.isArray(payload?.data) ? payload.data : [];
  const arr = [];

  for (let i = 0; i < schools.length; i++) {
    const school = schools[i];
    const teachers = Array.isArray(school?.teachers) ? school.teachers : [];

    for (let j = 0; j < teachers.length; j++) {
      const teacher = teachers[j];

      arr.push({
        teacher_id: teacher?.teacher_id ? Number(teacher.teacher_id) : null,
        first_name: teacher?.first_name || "",
        last_name: teacher?.last_name || "",
        email: teacher?.email || "",
        school_id: teacher?.school_id || school?.school_id || "",
        username: teacher?.username || "",
        password: teacher?.password || "",
      });
    }
  }

  return arr;
};

const getResponseHeaderValue = (headers, key) => {
  if (!headers) return "";
  return headers[key] || headers[String(key).toLowerCase()] || "";
};

const parseBlobAsJson = async (blob) => {
  if (!blob) return null;

  try {
    const text = await blob.text();
    if (!text) return null;
    return JSON.parse(text);
  } catch (error) {
    return null;
  }
};

const extractFileNameFromDisposition = (contentDisposition) => {
  const value = String(contentDisposition || "");
  if (!value) return "";

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]).replace(/["]/g, "");
  }

  const standardMatch = value.match(/filename="?([^"]+)"?/i);
  if (standardMatch?.[1]) {
    return standardMatch[1];
  }

  return "";
};

const downloadBlobFile = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || "download.csv";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

export default function UserProfile() {
  const classes = useStyles();

  // Composant Find Student Tab
const FindStudentTab = () => {
  const [searchCriteria, setSearchCriteria] = useState({
    login_name: "",
    school_id: "",
    first_name: "",
    last_name: "",
    email: "",
    password: "",  // Ajout du champ password
  });
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearchInputChange = (e) => {
    const { name, value } = e.target;
    setSearchCriteria(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    const hasCriteria = Object.values(searchCriteria).some(val => val && val.trim() !== "");
    
    if (!hasCriteria) {
      toast.error("Please enter at least one search criterion.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    try {
      setSearching(true);
      setSearched(true);

      const params = new URLSearchParams();
      Object.entries(searchCriteria).forEach(([key, value]) => {
        if (value && value.trim()) {
          params.append(key, value.trim());
        }
      });

      const response = await axios.get(
        process.env.REACT_APP_API_URL + `school-directory/students/search?${params.toString()}`,
        { headers }
      );

      if (response?.data?.status === "success") {
        setSearchResults(response.data.data || []);
        
        if (response.data.data.length === 0) {
          toast.info(response.data.message || "No students found.", {
            position: toast.POSITION.TOP_CENTER,
          });
        } else {
          toast.success(`Found ${response.data.data.length} student(s).`, {
            position: toast.POSITION.TOP_CENTER,
          });
        }
      } else {
        setSearchResults([]);
        toast.error(response?.data?.message || "Search failed.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      console.error("Search error:", error);
      
      if (error?.response?.status === 404) {
        setSearchResults([]);
        toast.info(error?.response?.data?.message || "No students found.", {
          position: toast.POSITION.TOP_CENTER,
        });
      } else {
        toast.error(error?.response?.data?.message || "Failed to search students.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } finally {
      setSearching(false);
    }
  };

  const handleClear = () => {
    setSearchCriteria({
      login_name: "",
      school_id: "",
      first_name: "",
      last_name: "",
      email: "",
      password: "",  // Reset du password
    });
    setSearchResults([]);
    setSearched(false);
  };

  return (
    <div style={{ display: "flex", gap: "24px", padding: "16px" }}>
      {/* Left Column - Search Form */}
      <div style={{ flex: "0 0 300px", backgroundColor: "#f5f5f5", padding: "20px", borderRadius: "8px" }}>
        <Typography variant="h6" style={{ marginBottom: "20px", fontWeight: 600, color: "#6B9D94" }}>
          Search Students
        </Typography>
        
        <TextField
          label="Login Name"
          name="login_name"
          variant="outlined"
          fullWidth
          size="small"
          className={classes.inputSpacing}
          value={searchCriteria.login_name}
          onChange={handleSearchInputChange}
          placeholder="Enter login name"
        />

        <TextField
          label="School ID"
          name="school_id"
          variant="outlined"
          fullWidth
          size="small"
          className={classes.inputSpacing}
          value={searchCriteria.school_id}
          onChange={handleSearchInputChange}
          placeholder="Enter school ID"
        />

        <TextField
          label="First Name"
          name="first_name"
          variant="outlined"
          fullWidth
          size="small"
          className={classes.inputSpacing}
          value={searchCriteria.first_name}
          onChange={handleSearchInputChange}
          placeholder="Enter first name"
        />

        <TextField
          label="Last Name"
          name="last_name"
          variant="outlined"
          fullWidth
          size="small"
          className={classes.inputSpacing}
          value={searchCriteria.last_name}
          onChange={handleSearchInputChange}
          placeholder="Enter last name"
        />

        <TextField
          label="Email"
          name="email"
          variant="outlined"
          fullWidth
          size="small"
          className={classes.inputSpacing}
          value={searchCriteria.email}
          onChange={handleSearchInputChange}
          placeholder="Enter email address"
        />

        {/* Ajout du champ Password dans le formulaire de recherche */}
        <TextField
          label="Password"
          name="password"
          variant="outlined"
          fullWidth
          size="small"
          className={classes.inputSpacing}
          value={searchCriteria.password}
          onChange={handleSearchInputChange}
          placeholder="Enter password"
          type="password"
        />

        <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
          <MuiButton
            variant="contained"
            onClick={handleSearch}
            disabled={searching}
            className={classes.systemButton}
            style={{ flex: 1 }}
          >
            {searching ? "Searching..." : "Search"}
          </MuiButton>
          
          <MuiButton
            variant="outlined"
            onClick={handleClear}
            disabled={searching}
            style={{ flex: 1 }}
          >
            Clear
          </MuiButton>
        </div>

        <Typography variant="caption" style={{ display: "block", marginTop: "16px", color: "#666" }}>
          * At least one field is required
        </Typography>
      </div>

      {/* Right Column - Results */}
      <div style={{ flex: 1, backgroundColor: "#fff", padding: "20px", borderRadius: "8px", border: "1px solid #e0e0e0" }}>
        <Typography variant="h6" style={{ marginBottom: "16px", fontWeight: 600, color: "#6B9D94" }}>
          Search Results {searched && `(${searchResults.length} found)`}
        </Typography>

        {!searched ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
            <Typography variant="body1">
              Enter search criteria and click "Search" to find students.
            </Typography>
          </div>
        ) : searchResults.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
            <Typography variant="body1">
              No students found matching your criteria.
            </Typography>
          </div>
        ) : (
          <div style={{ maxHeight: "600px", overflowY: "auto" }}>
            {searchResults.map((student, index) => (
              <div
                key={student.student_id || index}
                style={{
                  backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#fff",
                  padding: "16px",
                  marginBottom: "12px",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
                  <div>
                    <Typography variant="caption" style={{ color: "#666", display: "block" }}>
                      First Name
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {student.first_name || "N/A"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" style={{ color: "#666", display: "block" }}>
                      Last Name
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {student.last_name || "N/A"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" style={{ color: "#666", display: "block" }}>
                      Email
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {student.email || "N/A"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" style={{ color: "#666", display: "block" }}>
                      Login Name
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {student.login_name || "N/A"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" style={{ color: "#666", display: "block" }}>
                      Password
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 500, fontFamily: "monospace", backgroundColor: "#f0f0f0", padding: "2px 6px", borderRadius: "4px", display: "inline-block" }}>
                      {student.password || "N/A"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" style={{ color: "#666", display: "block" }}>
                      School ID
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {student.school_id || "N/A"}
                    </Typography>
                  </div>
                </div>
                
                {student.json_data && (
                  <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px dashed #ddd" }}>
                    <Typography variant="caption" style={{ color: "#999", display: "block" }}>
                      Additional Info from JSON
                    </Typography>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginTop: "8px" }}>
                      {student.json_data.teacher_id && (
                        <Typography variant="caption">
                          Teacher ID: {student.json_data.teacher_id}
                        </Typography>
                      )}
                      {student.json_data.teacher_group_id && (
                        <Typography variant="caption">
                          Group ID: {student.json_data.teacher_group_id}
                        </Typography>
                      )}
                      {student.json_data.organization && (
                        <Typography variant="caption">
                          Organization: {student.json_data.organization}
                        </Typography>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
// Support Tab Component
// Support Tab Component - Version corrigée
const SupportTab = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);

  // Récupérer la liste des étudiants qui ont des conversations
  const fetchConversations = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      setLoadingConversations(true);
      const response = await axios.get(
        process.env.REACT_APP_API_URL + "support/conversations",
        { headers }
      );

      if (response?.data?.status === "success") {
        // Vérifier que data est un tableau
        const data = response.data.data;
        if (Array.isArray(data)) {
          setConversations(data);
        } else {
          setConversations([]);
        }
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
      toast.error("Failed to load conversations.", {
        position: toast.POSITION.TOP_CENTER,
      });
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  };

  // Récupérer la conversation avec un étudiant spécifique
  const fetchConversationWithStudent = async (studentId) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      setLoadingMessages(true);
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `support/conversations/${studentId}`,
        { headers }
      );

      if (response?.data?.status === "success") {
        const data = response.data.data;
        // Vérifier si data.messages existe (nouveau format) ou si data est directement le tableau
        if (data && Array.isArray(data.messages)) {
          setMessages(data.messages);
          // Mettre à jour l'étudiant sélectionné avec les infos complètes si disponibles
          if (data.student && !selectedStudent?.student_name) {
            setSelectedStudent(prev => ({
              ...prev,
              student_name: data.student.student_name,
              email: data.student.email,
              school_id: data.student.school_id,
              login_name: data.student.login_name
            }));
          }
        } else if (Array.isArray(data)) {
          setMessages(data);
        } else {
          setMessages([]);
        }
      }
    } catch (error) {
      console.error("Error fetching conversation:", error);
      toast.error("Failed to load conversation.", {
        position: toast.POSITION.TOP_CENTER,
      });
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Envoyer une réponse du support
  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.error("Please enter a reply message.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    if (!selectedStudent) {
      toast.error("No student selected.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      setSendingReply(true);
      const response = await axios.post(
        process.env.REACT_APP_API_URL + "support/messages/reply",
        {
          student_id: selectedStudent.student_id,
          message: replyText.trim(),
        },
        { headers }
      );

      if (response?.data?.status === "success") {
        toast.success("Reply sent successfully.", {
          position: toast.POSITION.TOP_CENTER,
        });
        setReplyText("");
        // Recharger la conversation pour voir le nouveau message
        await fetchConversationWithStudent(selectedStudent.student_id);
        // Recharger aussi la liste des conversations pour mettre à jour le dernier message
        await fetchConversations();
      } else {
        toast.error(response?.data?.message || "Failed to send reply.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      console.error("Error sending reply:", error);
      toast.error(error?.response?.data?.message || "Failed to send reply.", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setSendingReply(false);
    }
  };

  // Sélectionner un étudiant
  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setMessages([]);
    fetchConversationWithStudent(student.student_id);
  };

  // Formater la date
  const formatMessageDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  return (
    <div style={{ display: "flex", gap: "24px", minHeight: "600px", padding: "16px" }}>
      {/* Left Column - Liste des étudiants */}
      <div
        style={{
          flex: "0 0 350px",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          border: "1px solid #e0e0e0",
        }}
      >
        <div
          style={{
            padding: "16px",
            backgroundColor: "#6B9D94",
            color: "#fff",
            fontWeight: 600,
            borderBottom: "1px solid #5a8a80",
          }}
        >
          Conversations ({conversations.length})
        </div>

        <div style={{ flex: 1, overflowY: "auto", maxHeight: "calc(100vh - 300px)" }}>
          {loadingConversations ? (
            <div style={{ padding: "40px", textAlign: "center", color: "#666" }}>
              Loading...
            </div>
          ) : conversations.length === 0 ? (
            <div style={{ padding: "40px", textAlign: "center", color: "#666" }}>
              No conversations yet.
            </div>
          ) : (
            conversations.map((student, idx) => (
              <div
                key={student.student_id || idx}
                onClick={() => handleSelectStudent(student)}
                style={{
                  padding: "16px",
                  borderBottom: "1px solid #e0e0e0",
                  cursor: "pointer",
                  backgroundColor:
                    selectedStudent?.student_id === student.student_id
                      ? "#e0f7fa"
                      : "transparent",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (selectedStudent?.student_id !== student.student_id) {
                    e.currentTarget.style.backgroundColor = "#f0f0f0";
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedStudent?.student_id !== student.student_id) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                      {student.student_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || student.email?.split('@')[0] || "Unknown"}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                      {student.email || "No email"}
                    </div>
                    <div style={{ fontSize: "11px", color: "#999" }}>
                      ID: {student.student_id}
                    </div>
                  </div>
                  {student.unread_count > 0 && (
                    <div
                      style={{
                        backgroundColor: "#d32f2f",
                        color: "#fff",
                        borderRadius: "50%",
                        minWidth: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "bold",
                        padding: "0 6px",
                      }}
                    >
                      {student.unread_count}
                    </div>
                  )}
                </div>
                {student.last_message && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      marginTop: "8px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontStyle: "italic",
                    }}
                  >
                    📝 {student.last_message.substring(0, 60)}
                    {student.last_message.length > 60 ? "..." : ""}
                  </div>
                )}
                {student.last_message_date && (
                  <div style={{ fontSize: "10px", color: "#aaa", marginTop: "4px" }}>
                    🕒 {formatMessageDate(student.last_message_date)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Column - Conversation et réponse */}
      <div
        style={{
          flex: 1,
          backgroundColor: "#fff",
          borderRadius: "8px",
          border: "1px solid #e0e0e0",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {!selectedStudent ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
              padding: "40px",
            }}
          >
            Select a conversation from the left to view messages.
          </div>
        ) : (
          <>
            {/* Header */}
            <div
              style={{
                padding: "16px",
                backgroundColor: "#6B9D94",
                color: "#fff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: "16px" }}>
                  {selectedStudent.student_name || `${selectedStudent.first_name || ''} ${selectedStudent.last_name || ''}`.trim() || selectedStudent.email || "Student"}
                </div>
                <div style={{ fontSize: "12px", opacity: 0.9 }}>
                  {selectedStudent.email || "No email"} | ID: {selectedStudent.student_id}
                </div>
                {selectedStudent.school_id && (
                  <div style={{ fontSize: "11px", opacity: 0.8 }}>
                    School: {selectedStudent.school_id}
                  </div>
                )}
              </div>
            </div>

            {/* Messages */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "20px",
                backgroundColor: "#fafafa",
                minHeight: "400px",
                maxHeight: "calc(100vh - 450px)",
              }}
            >
              {loadingMessages ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                  Loading messages...
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                  No messages yet. Start the conversation!
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    style={{
                      display: "flex",
                      justifyContent: msg.direction === "student_to_support" ? "flex-start" : "flex-end",
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "75%",
                        padding: "12px 16px",
                        borderRadius: "12px",
                        backgroundColor:
                          msg.direction === "student_to_support" ? "#e8e8e8" : "#6B9D94",
                        color: msg.direction === "student_to_support" ? "#000" : "#fff",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "10px",
                          marginBottom: "6px",
                          fontWeight: 500,
                          color: msg.direction === "student_to_support" ? "#666" : "#e0e0e0",
                        }}
                      >
                        {msg.direction === "student_to_support" ? "👤 Student" : "🛟 Support"} • {formatMessageDate(msg.created_at)}
                      </div>
                      <div style={{ fontSize: "14px", lineHeight: "1.5", wordBreak: "break-word" }}>
                        {msg.message}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Reply Box */}
            <div
              style={{
                padding: "16px",
                borderTop: "1px solid #e0e0e0",
                backgroundColor: "#fff",
              }}
            >
              <TextField
                label="Reply to student"
                placeholder="Type your response here..."
                multiline
                rows={3}
                variant="outlined"
                fullWidth
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                disabled={sendingReply}
                style={{ marginBottom: "12px" }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <MuiButton
                  variant="contained"
                  className={classes.systemButton}
                  onClick={handleSendReply}
                  disabled={sendingReply || !replyText.trim()}
                >
                  {sendingReply ? "Sending..." : "Send Reply"}
                </MuiButton>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
  const [allStudents, setAllStudents] = useState([]);
  const [allIndependentReaders, setAllIndependentReaders] = useState([]);
  const [allAdmin, setAllAdmin] = useState([]);
  const [allParents, setAllParents] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allOrganizations, setAllOrganizations] = useState([]);

  const [allSchoolStudents, setAllSchoolStudents] = useState([]);
  const [allSchoolTeachers, setAllSchoolTeachers] = useState([]);

  const [schoolStudentTeacherOptions, setSchoolStudentTeacherOptions] =
    useState([]);
  const [bulkSchoolTeacherOptions, setBulkSchoolTeacherOptions] = useState([]);

  const [loadingSchoolStudents, setLoadingSchoolStudents] = useState(false);
  const [loadingSchoolTeachers, setLoadingSchoolTeachers] = useState(false);
  const [loadingSchoolStudentTeacherOptions, setLoadingSchoolStudentTeacherOptions] =
    useState(false);
  const [loadingBulkSchoolTeacherOptions, setLoadingBulkSchoolTeacherOptions] =
    useState(false);

  const [allStats, setAllStats] = useState([]);
  const [loadingAllStats, setLoadingAllStats] = useState(false);

  const [openMessage, setOpenMessage] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState({
    first_name: "",
    last_name: "",
    email: "",
    group: "",
  });
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  const [openReport, setOpenReport] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [selectedStudentForReport, setSelectedStudentForReport] = useState({
    student_id: null,
    login_name: "",
    first_name: "",
    last_name: "",
    books: [],
    overall: { ...EMPTY_OVERALL },
  });

  const [openAddIndependentReader, setOpenAddIndependentReader] =
    useState(false);
  const [creatingIndependentReader, setCreatingIndependentReader] =
    useState(false);
  const [independentReaderForm, setIndependentReaderForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  const [openAddSchoolStudent, setOpenAddSchoolStudent] = useState(false);
  const [creatingSchoolStudent, setCreatingSchoolStudent] = useState(false);
  const [schoolStudentForm, setSchoolStudentForm] = useState({
    school_id: "",
    teacher_id: "",
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    password: "",
  });

  const [openBulkUploadSchoolStudents, setOpenBulkUploadSchoolStudents] =
    useState(false);
  const [bulkUploadingSchoolStudents, setBulkUploadingSchoolStudents] =
    useState(false);
  const [bulkSchoolStudentForm, setBulkSchoolStudentForm] = useState({
    school_id: "",
    teacher_id: "",
    file: null,
  });

  const [openAddTeacher, setOpenAddTeacher] = useState(false);
  const [creatingTeacher, setCreatingTeacher] = useState(false);
  const [teacherForm, setTeacherForm] = useState({
    school_id: "",
    firstName: "",
    lastName: "",
    email: "",
  });

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deletingItem, setDeletingItem] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({
    type: "",
    id: null,
    email: "",
    label: "",
  });

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const formatBookTitle = (value) => {
    if (!value) return "N/A";
    return String(value).replace(/\.pdf$/i, "");
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";

    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }).format(date);
  };

  const resetReportState = () => {
    setSelectedStudentForReport({
      student_id: null,
      login_name: "",
      first_name: "",
      last_name: "",
      books: [],
      overall: { ...EMPTY_OVERALL },
    });
  };

  const isAuthorized = () => {
    if (new Date() > new Date(currentUser.expiresInTime)) {
      toast.error("Unauthorized User. Please log In", {
        position: toast.POSITION.TOP_CENTER,
      });
      localStorage.removeItem("user");
      window.location.href = "/";
      return false;
    }
    return true;
  };

  const getAuthHeaders = () => {
    if (!isAuthorized()) return null;

    return {
      "Content-Type": "application/json",
      Authorization: "Bearer " + currentUser.accessToken,
    };
  };

  const getMultipartAuthHeaders = () => {
    if (!isAuthorized()) return null;

    return {
      Authorization: "Bearer " + currentUser.accessToken,
    };
  };

  const handleOpenMessage = (student) => {
    setSelectedStudent({
      first_name: student.student_first_name || "",
      last_name: student.student_last_name || "",
      email: student.student_email || "",
      group: student.product_code || "",
    });

    setMessageText("");
    setOpenMessage(true);
  };

const fetchAllStats = async (studentId = null) => {
  try {
    setLoadingAllStats(true);
    
    let url;
    if (studentId) {
      // Utilisez la variable studentId, pas une valeur en dur
      url = `${process.env.REACT_APP_API_URL_STAT}stats?page=1&page_size=30&student_id=${studentId}`;
    } else {
      // Si pas de studentId, on retourne un tableau vide
      setAllStats([]);
      return [];
    }
    
    const response = await retryAxiosGet(url);
    const items = Array.isArray(response?.data?.items) ? response.data.items : [];
    setAllStats(items);
    return items;
    
  } catch (error) {
    setAllStats([]);
    toast.error("Failed to load reading stats.", {
      position: toast.POSITION.TOP_CENTER,
    });
    return [];
  } finally {
    setLoadingAllStats(false);
  }
};

const handleViewReport = async (student) => {
  setOpenReport(true);
  setLoadingReport(true);

  const studentId = Number(student.student_id);
  const loginName = String(
    student.login_name || student.student_login || ""
  ).trim().toLowerCase();

  try {
    // L'API retourne déjà uniquement les stats de cet étudiant
    const statsSource = await fetchAllStats(studentId);

    // Plus besoin de filtrer, on utilise directement les données
    const books = statsSource.map((item, index) => ({
      id: `${item.id || index}-${item.student_id || "student"}`,
      book_filename: item.book_filename || `book-${index + 1}`,
      book_title: formatBookTitle(item.book_title || item.book_filename),
      completion_percent: toNumber(item.completion_percent),
      minutes_per_day: toNumber(
        item.minutes_today ?? item.minutes_per_day ?? item.minutes
      ),
      wpm: toNumber(item.wpm),
      comprehension: toNumber(
        item.average_quiz_score ?? item.comprehension ?? item.quiz_score
      ),
      fluency_score: toNumber(item.fluency_score),
    }));

    const totalBooks = books.length;

    const overall = totalBooks > 0
      ? {
          total_books: totalBooks,
          avg_completion:
            books.reduce((sum, book) => sum + toNumber(book.completion_percent), 0) / totalBooks,
          avg_minutes_per_day:
            books.reduce((sum, book) => sum + toNumber(book.minutes_per_day), 0) / totalBooks,
          avg_wpm:
            books.reduce((sum, book) => sum + toNumber(book.wpm), 0) / totalBooks,
          avg_comprehension:
            books.reduce((sum, book) => sum + toNumber(book.comprehension), 0) / totalBooks,
          avg_fluency:
            books.reduce((sum, book) => sum + toNumber(book.fluency_score), 0) / totalBooks,
        }
      : { ...EMPTY_OVERALL };

    setSelectedStudentForReport({
      student_id: studentId,
      login_name: loginName,
      first_name: student.student_first_name || "",
      last_name: student.student_last_name || "",
      books,
      overall,
    });
  } catch (error) {
    console.error("Error preparing report:", error);
    toast.error("Failed to load reading report.", {
      position: toast.POSITION.TOP_CENTER,
    });

    setSelectedStudentForReport({
      student_id: studentId,
      login_name: loginName,
      first_name: student.student_first_name || "",
      last_name: student.student_last_name || "",
      books: [],
      overall: { ...EMPTY_OVERALL },
    });
  } finally {
    setLoadingReport(false);
  }
};

  const onCloseMessage = () => {
    if (sendingMessage) return;

    setOpenMessage(false);
    setMessageText("");
    setSelectedStudent({
      first_name: "",
      last_name: "",
      email: "",
      group: "",
    });
  };

  const onCloseReport = () => {
    setOpenReport(false);
    setLoadingReport(false);
    resetReportState();
  };

  const handleSendMessage = async (scope) => {
    const cleanMessage = String(messageText || "").trim();

    if (!cleanMessage) {
      toast.error("Please enter a message first.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    if (scope === "one" && !selectedStudent.email) {
      toast.error("Selected student email is missing.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    const payload =
      scope === "one"
        ? {
            email: selectedStudent.email,
            message: cleanMessage,
          }
        : {
            message: cleanMessage,
          };

    try {
      setSendingMessage(true);

      const response = await axios.post(
        process.env.REACT_APP_API_URL + `eop/messages/${scope}`,
        payload,
        { headers }
      );

      const apiData = response?.data || {};

      if (
        response.status >= 200 &&
        response.status < 300 &&
        (apiData.status === "success" ||
          apiData.code === 201 ||
          apiData.code === 200)
      ) {
        toast.success(
          scope === "one"
            ? `Message sent to ${selectedStudent.first_name} ${selectedStudent.last_name}`
            : "Message sent to all students",
          {
            position: toast.POSITION.TOP_CENTER,
          }
        );

        onCloseMessage();
        return;
      }

      toast.error(apiData.message || "Failed to send message.", {
        position: toast.POSITION.TOP_CENTER,
      });
    } catch (error) {
      console.error("Send message error:", error);

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to send message";

      toast.error(backendMessage, {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setSendingMessage(false);
    }
  };

  const getAllUsers = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const data1 = await retryFetchJson(
        process.env.REACT_APP_API_URL + `users/${currentUser.organization}`,
        { headers }
      );

      const arr = [];
      const admin = [];
      const student = [];
      const parent = [];
      const teacher = [];
      const allOrga = [];
      const data = data1.data || [];

      if (
        currentUser.role === "organization" ||
        currentUser.role === "admin" ||
        currentUser.role === "teacher"
      ) {
        for (let i = 0; i < data.length; i++) {
          if (
            data[i].role === "student" &&
            data[i].organization === currentUser.organization
          ) {
            student.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
            arr.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
          } else if (
            data[i].role === "teacher" &&
            data[i].organization === currentUser.organization
          ) {
            teacher.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
            arr.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
          } else if (
            data[i].role === "parent" &&
            data[i].organization === currentUser.organization
          ) {
            parent.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
            arr.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
          } else if (
            (data[i].role === "admin" || data[i].role === "organization") &&
            data[i].organization === currentUser.organization
          ) {
            admin.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
            arr.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
          }
        }
      } else if (
        currentUser.role === "student" ||
        currentUser.role === "parent"
      ) {
        for (let i = 0; i < data.length; i++) {
          if (
            data[i].role === "student" &&
            data[i].organization === currentUser.organization
          ) {
            student.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
            arr.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
          } else if (
            data[i].role === "teacher" &&
            data[i].organization === currentUser.organization
          ) {
            teacher.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
            arr.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
          }
        }
      } else {
        for (let i = 0; i < data.length; i++) {
          if (data[i].role === "student") {
            student.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
          } else if (data[i].role === "teacher") {
            teacher.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
          } else if (data[i].role === "parent") {
            parent.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
          } else if (
            data[i].role === "admin" ||
            data[i].role === "organization"
          ) {
            admin.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
            allOrga.push({
              value: data[i].organization,
              label: data[i].organization,
            });
          }

          arr.push({
            email: data[i].email,
            organization: data[i].organization,
            role: data[i].role,
          });
        }
      }

      setAllUsers(arr);
      setAllStudents(student);
      setAllTeachers(teacher);
      setAllAdmin(admin);
      setAllParents(parent);
      setAllOrganizations(allOrga);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error(
        error?.response?.data?.message || "Failed to load users.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
    }
  };

  const getIndependentReaders = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const data1 = await retryFetchJson(
        process.env.REACT_APP_API_URL + `eop/users/latest-bookmarks`,
        { headers }
      );

      const arr = [];
      const data = data1.data || [];

      for (let i = 0; i < data.length; i++) {
        const latestBookmark = data[i].latestBookmark || {};

        arr.push({
          student_id: data[i].student_id ? Number(data[i].student_id) : null,
          login_name:
            data[i].login_name ||
            data[i].student_login ||
            data[i].username ||
            latestBookmark.login_name ||
            "",
          student_email: data[i].student_email || data[i].email || "",
          student_first_name:
            data[i].student_first_name || data[i].first_name || "",
          student_last_name:
            data[i].student_last_name || data[i].last_name || "",
          product_code: data[i].product_code || "",
          title: latestBookmark.book_filename
            ? formatBookTitle(latestBookmark.book_filename)
            : data[i].title || "N/A",
          author: data[i].author || "N/A",
          start_date: formatDate(latestBookmark.created_at || data[i].created_at),
          end_date: data[i].end_date || "N/A",
        });
      }

      setAllIndependentReaders(arr);
    } catch (error) {
      console.error("Error fetching independent readers:", error);
      toast.error(
        error?.response?.data?.message ||
          "Failed to load independent readers.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
    }
  };

  const getSchoolStudents = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      setLoadingSchoolStudents(true);

      const response = await retryAxiosGet(
        process.env.REACT_APP_API_URL + "school-directory/students",
        { headers }
      );

      const flattened = flattenSchoolStudentsResponse(response?.data);
      setAllSchoolStudents(flattened);
    } catch (error) {
      console.error("Error fetching school students:", error);
      setAllSchoolStudents([]);
      toast.error(
        error?.response?.data?.message || "Failed to load school students.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
    } finally {
      setLoadingSchoolStudents(false);
    }
  };

  const getSchoolTeachers = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      setLoadingSchoolTeachers(true);

      const response = await retryAxiosGet(
        process.env.REACT_APP_API_URL + "school-directory/teachers",
        { headers }
      );

      const flattened = flattenSchoolTeachersResponse(response?.data);
      setAllSchoolTeachers(flattened);
    } catch (error) {
      console.error("Error fetching school teachers:", error);
      setAllSchoolTeachers([]);
      toast.error(
        error?.response?.data?.message || "Failed to load school teachers.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
    } finally {
      setLoadingSchoolTeachers(false);
    }
  };

  const fetchTeachersForSchool = async ({
    schoolId,
    setOptions,
    setLoading,
  }) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      setLoading(true);

      const normalizedSchoolId = normalizeSchoolIdValue(schoolId);

      const response = await retryAxiosGet(
        process.env.REACT_APP_API_URL + "school-directory/teachers",
        {
          headers,
          params: {
            school_id: normalizedSchoolId,
          },
        }
      );

      const flattened = flattenSchoolTeachersResponse(response?.data);
      setOptions(flattened);
    } catch (error) {
      console.error("Error fetching teachers for school:", error);
      setOptions([]);
      toast.error(
        error?.response?.data?.message || "Failed to load teachers.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddIndependentReader = () => {
    setIndependentReaderForm({
      firstName: "",
      lastName: "",
      email: "",
    });
    setOpenAddIndependentReader(true);
  };

  const handleCloseAddIndependentReader = () => {
    if (creatingIndependentReader) return;
    setOpenAddIndependentReader(false);
    setIndependentReaderForm({
      firstName: "",
      lastName: "",
      email: "",
    });
  };

  const handleCreateIndependentReader = async () => {
    const firstName = String(independentReaderForm.firstName || "").trim();
    const lastName = String(independentReaderForm.lastName || "").trim();
    const email = String(independentReaderForm.email || "").trim();

    if (!firstName || !lastName || !email) {
      toast.error("Please fill First Name, Last Name and Email Address.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      setCreatingIndependentReader(true);

      const response = await axios.post(
        process.env.REACT_APP_API_URL + "school-directory/independent-readers",
        {
          firstName,
          lastName,
          email,
        },
        { headers }
      );

      if (
        response.status >= 200 &&
        response.status < 300 &&
        (response?.data?.status === "success" ||
          response?.data?.code === 201 ||
          response?.data?.code === 200)
      ) {
        toast.success("Independent Reader added successfully.", {
          position: toast.POSITION.TOP_CENTER,
        });
        handleCloseAddIndependentReader();
        await getIndependentReaders();
      } else {
        toast.error(response?.data?.message || "Failed to add student.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      console.error("Error creating independent reader:", error);
      toast.error(
        error?.response?.data?.message || "Failed to add Independent Reader.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
    } finally {
      setCreatingIndependentReader(false);
    }
  };

  const handleOpenAddSchoolStudent = () => {
    setSchoolStudentForm({
      school_id: "",
      teacher_id: "",
      firstName: "",
      lastName: "",
      email: "",
      username: "",
      password: "",
    });
    setSchoolStudentTeacherOptions([]);
    setOpenAddSchoolStudent(true);
  };

  const handleCloseAddSchoolStudent = () => {
    if (creatingSchoolStudent) return;
    setOpenAddSchoolStudent(false);
    setSchoolStudentForm({
      school_id: "",
      teacher_id: "",
      firstName: "",
      lastName: "",
      email: "",
      username: "",
      password: "",
    });
    setSchoolStudentTeacherOptions([]);
  };

  const handleCreateSchoolStudent = async () => {
    const school_id = normalizeSchoolIdValue(schoolStudentForm.school_id);
    const teacher_id = String(schoolStudentForm.teacher_id || "").trim();
    const firstName = String(schoolStudentForm.firstName || "").trim();
    const lastName = String(schoolStudentForm.lastName || "").trim();
    const email = String(schoolStudentForm.email || "").trim();
    const username = String(schoolStudentForm.username || email).trim();
    const password = String(schoolStudentForm.password || firstName).trim();

    if (!school_id || !teacher_id || !firstName || !lastName || !email) {
      toast.error(
        "Please fill School ID, Teacher, First Name, Last Name and Email Address.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    const payload = {
      school_id,
      teacher_id: Number(teacher_id),
      first_name: firstName,
      last_name: lastName,
      email,
      username,
      password,
    };

    try {
      setCreatingSchoolStudent(true);

      const response = await axios.post(
        process.env.REACT_APP_API_URL + "eop/students/register",
        payload,
        { headers }
      );

      if (
        response.status >= 200 &&
        response.status < 300 &&
        (response?.data?.status === "success" ||
          response?.data?.code === 201 ||
          response?.data?.code === 200)
      ) {
        toast.success("School Student added successfully.", {
          position: toast.POSITION.TOP_CENTER,
        });
        handleCloseAddSchoolStudent();
        await getSchoolStudents();
        await getSchoolTeachers();
      } else {
        toast.error(response?.data?.message || "Failed to add school student.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      console.error("Error creating school student:", error);
      toast.error(
        error?.response?.data?.message || "Failed to add School Student.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
    } finally {
      setCreatingSchoolStudent(false);
    }
  };

  const handleOpenBulkUploadSchoolStudents = () => {
    setBulkSchoolStudentForm({
      school_id: "",
      teacher_id: "",
      file: null,
    });
    setBulkSchoolTeacherOptions([]);
    setOpenBulkUploadSchoolStudents(true);
  };

  const handleCloseBulkUploadSchoolStudents = () => {
    if (bulkUploadingSchoolStudents) return;

    setOpenBulkUploadSchoolStudents(false);
    setBulkSchoolStudentForm({
      school_id: "",
      teacher_id: "",
      file: null,
    });
    setBulkSchoolTeacherOptions([]);
  };

  const handleBulkSchoolStudentFileChange = (event) => {
    const file = event?.target?.files?.[0] || null;

    setBulkSchoolStudentForm((prev) => ({
      ...prev,
      file,
    }));
  };

  const handleBulkUploadSchoolStudents = async () => {
    const school_id = normalizeSchoolIdValue(bulkSchoolStudentForm.school_id);
    const teacher_id = String(bulkSchoolStudentForm.teacher_id || "").trim();
    const file = bulkSchoolStudentForm.file;

    if (!school_id || !teacher_id || !file) {
      toast.error("Please fill School ID, Teacher and choose a CSV file.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    const multipartHeaders = getMultipartAuthHeaders();
    if (!multipartHeaders) return;

    const formData = new FormData();
    formData.append("school_id", school_id);
    formData.append("teacher_id", String(Number(teacher_id)));
    formData.append("file", file);

    try {
      setBulkUploadingSchoolStudents(true);

      const response = await axios.post(
        process.env.REACT_APP_API_URL + "school-directory/students/bulk-upload",
        formData,
        {
          headers: multipartHeaders,
          responseType: "blob",
        }
      );

      const contentType = String(
        getResponseHeaderValue(response?.headers, "content-type")
      ).toLowerCase();

      const isCsvResponse =
        contentType.includes("text/csv") ||
        contentType.includes("application/csv") ||
        contentType.includes("application/octet-stream");

      if (isCsvResponse) {
        const disposition = getResponseHeaderValue(
          response?.headers,
          "content-disposition"
        );
        const fileName =
          extractFileNameFromDisposition(disposition) ||
          `bulk-student-upload-errors-${Date.now()}.csv`;

        downloadBlobFile(response.data, fileName);

        const savedCount = Number(
          getResponseHeaderValue(response?.headers, "x-saved-count") || 0
        );
        const failedCount = Number(
          getResponseHeaderValue(response?.headers, "x-failed-count") || 0
        );

        if (savedCount > 0 && failedCount > 0) {
          toast.warn(
            `${savedCount} students saved, ${failedCount} failed. Error CSV downloaded.`,
            {
              position: toast.POSITION.TOP_CENTER,
            }
          );
        } else if (failedCount > 0) {
          toast.warn(
            `${failedCount} rows failed. Error CSV downloaded.`,
            {
              position: toast.POSITION.TOP_CENTER,
            }
          );
        } else {
          toast.warn("Bulk upload finished with downloadable response.", {
            position: toast.POSITION.TOP_CENTER,
          });
        }

        handleCloseBulkUploadSchoolStudents();
        await getSchoolStudents();
        return;
      }

      const jsonData = await parseBlobAsJson(response.data);

      if (
        response.status >= 200 &&
        response.status < 300 &&
        (jsonData?.status === "success" ||
          jsonData?.code === 201 ||
          jsonData?.code === 200)
      ) {
        toast.success(
          jsonData?.message || "Bulk upload completed successfully.",
          {
            position: toast.POSITION.TOP_CENTER,
          }
        );
        handleCloseBulkUploadSchoolStudents();
        await getSchoolStudents();
      } else {
        toast.error(
          jsonData?.message || "Bulk upload did not complete successfully.",
          {
            position: toast.POSITION.TOP_CENTER,
          }
        );
      }
    } catch (error) {
      console.error("Error bulk uploading school students:", error);

      let backendMessage = "Failed to bulk upload school students.";

      if (error?.response?.data instanceof Blob) {
        const parsedError = await parseBlobAsJson(error.response.data);
        backendMessage =
          parsedError?.message || parsedError?.error || backendMessage;
      } else {
        backendMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          backendMessage;
      }

      toast.error(backendMessage, {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setBulkUploadingSchoolStudents(false);
    }
  };

  const handleOpenAddTeacher = () => {
    setTeacherForm({
      school_id: "",
      firstName: "",
      lastName: "",
      email: "",
    });
    setOpenAddTeacher(true);
  };

  const handleCloseAddTeacher = () => {
    if (creatingTeacher) return;
    setOpenAddTeacher(false);
    setTeacherForm({
      school_id: "",
      firstName: "",
      lastName: "",
      email: "",
    });
  };

  const handleCreateTeacher = async () => {
    const school_id = normalizeSchoolIdValue(teacherForm.school_id);
    const firstName = String(teacherForm.firstName || "").trim();
    const lastName = String(teacherForm.lastName || "").trim();
    const email = String(teacherForm.email || "").trim();

    if (!school_id || !firstName || !lastName || !email) {
      toast.error("Please fill School ID, First Name, Last Name and Email Address.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      setCreatingTeacher(true);

      const response = await axios.post(
        process.env.REACT_APP_API_URL + "school-directory/teachers",
        {
          school_id,
          firstName,
          lastName,
          email,
        },
        { headers }
      );

      if (
        response.status >= 200 &&
        response.status < 300 &&
        (response?.data?.status === "success" ||
          response?.data?.code === 201 ||
          response?.data?.code === 200)
      ) {
        toast.success("Teacher added successfully.", {
          position: toast.POSITION.TOP_CENTER,
        });
        handleCloseAddTeacher();
        await getSchoolTeachers();
      } else {
        toast.error(response?.data?.message || "Failed to add teacher.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      console.error("Error creating teacher:", error);
      toast.error(
        error?.response?.data?.message || "Failed to add Teacher.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
    } finally {
      setCreatingTeacher(false);
    }
  };

  const handleOpenDeleteConfirm = (type, row) => {
    if (type === "student") {
      const firstName = row.student_first_name || row.first_name || "";
      const lastName = row.student_last_name || row.last_name || "";
      const email = row.student_email || row.email || "";
      const id = row.student_id ? Number(row.student_id) : null;

      setDeleteTarget({
        type: "student",
        id,
        email,
        label: `${firstName} ${lastName}`.trim() || email || "this student",
      });
    } else if (type === "teacher") {
      const firstName = row.first_name || "";
      const lastName = row.last_name || "";
      const id = row.teacher_id ? Number(row.teacher_id) : null;

      setDeleteTarget({
        type: "teacher",
        id,
        email: row.email || "",
        label: `${firstName} ${lastName}`.trim() || row.email || "this teacher",
      });
    }

    setOpenDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    if (deletingItem) return;
    setOpenDeleteConfirm(false);
    setDeleteTarget({
      type: "",
      id: null,
      email: "",
      label: "",
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget.type || !deleteTarget.id) {
      toast.error("Invalid item selected for deletion.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      setDeletingItem(true);

      if (deleteTarget.type === "student") {
        await axios.delete(
          process.env.REACT_APP_API_URL +
            `school-directory/students/${deleteTarget.id}`,
          {
            headers,
            params: {
              email: deleteTarget.email,
            },
          }
        );

        toast.success("Student deleted successfully.", {
          position: toast.POSITION.TOP_CENTER,
        });

        await getIndependentReaders();
        await getSchoolStudents();
      } else {
        await axios.delete(
          process.env.REACT_APP_API_URL +
            `school-directory/teachers/${deleteTarget.id}`,
          {
            headers,
          }
        );

        toast.success("Teacher deleted successfully.", {
          position: toast.POSITION.TOP_CENTER,
        });

        await getSchoolTeachers();
        await getSchoolStudents();
      }

      handleCloseDeleteConfirm();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error(
        error?.response?.data?.message || "Failed to delete item.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
    } finally {
      setDeletingItem(false);
    }
  };

  useEffect(() => {
    if (!openAddSchoolStudent) return;

    const schoolId = normalizeSchoolIdValue(schoolStudentForm.school_id);

    if (!schoolId) {
      setSchoolStudentTeacherOptions([]);
      return;
    }

    const timer = setTimeout(() => {
      fetchTeachersForSchool({
        schoolId,
        setOptions: setSchoolStudentTeacherOptions,
        setLoading: setLoadingSchoolStudentTeacherOptions,
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [schoolStudentForm.school_id, openAddSchoolStudent]);

  useEffect(() => {
    if (!openBulkUploadSchoolStudents) return;

    const schoolId = normalizeSchoolIdValue(bulkSchoolStudentForm.school_id);

    if (!schoolId) {
      setBulkSchoolTeacherOptions([]);
      return;
    }

    const timer = setTimeout(() => {
      fetchTeachersForSchool({
        schoolId,
        setOptions: setBulkSchoolTeacherOptions,
        setLoading: setLoadingBulkSchoolTeacherOptions,
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [bulkSchoolStudentForm.school_id, openBulkUploadSchoolStudents]);

  useEffect(() => {
    const run = async () => {
      await Promise.allSettled([
        // getAllUsers(),
        getIndependentReaders(),
        getSchoolStudents(),
        getSchoolTeachers(),
        // fetchAllStats(),
      ]);
    };

    run();
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Organization",
        accessor: "organization",
      },
      {
        Header: "Role",
        accessor: "role",
      },
    ],
    []
  );

  const independentReaderColumns = useMemo(
    () => [
      {
        Header: "First Name",
        accessor: "student_first_name",
      },
      {
        Header: "Last Name",
        accessor: "student_last_name",
      },
      {
        Header: "Email",
        accessor: "student_email",
      },
      {
        Header: "Product",
        accessor: "product_code",
      },
      {
        Header: "Current Title",
        accessor: "title",
      },
      {
        Header: "Author",
        accessor: "author",
      },
      {
        Header: "Start",
        accessor: "start_date",
      },
      {
        Header: "MSG",
        accessor: "message",
        Cell: ({ row }) => (
          <MessageOutlined
            style={{ color: "grey", cursor: "pointer" }}
            onClick={() => handleOpenMessage(row.original)}
          />
        ),
      },
      {
        Header: "MRR",
        accessor: "mrr",
        Cell: ({ row }) => (
          <RemoveRedEye
            style={{
              color: loadingAllStats ? "#A0A0A0" : "grey",
              cursor: "pointer",
            }}
            onClick={() => handleViewReport(row.original)}
          />
        ),
      },
      {
        Header: "Delete",
        accessor: "delete",
        Cell: ({ row }) => (
          <Close
            className={classes.deleteXIcon}
            onClick={() => handleOpenDeleteConfirm("student", row.original)}
          />
        ),
      },
    ],
    [loadingAllStats, classes.deleteXIcon]
  );

  const schoolStudentColumns = useMemo(
    () => [
      {
        Header: "First Name",
        accessor: "first_name",
      },
      {
        Header: "Last Name",
        accessor: "last_name",
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "School ID",
        accessor: "school_id",
      },
      {
        Header: "Teacher Name",
        accessor: "teacher_name",
      },
      {
        Header: "Username",
        accessor: "username",
      },
      {
        Header: "Password",
        accessor: "password",
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ row }) => (
          <Close
            className={classes.deleteXIcon}
            onClick={() => handleOpenDeleteConfirm("student", row.original)}
          />
        ),
      },
    ],
    [classes.deleteXIcon]
  );

  const schoolTeacherColumns = useMemo(
    () => [
      {
        Header: "First Name",
        accessor: "first_name",
      },
      {
        Header: "Last Name",
        accessor: "last_name",
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "School ID",
        accessor: "school_id",
      },
      {
        Header: "Username",
        accessor: "username",
      },
      {
        Header: "Password",
        accessor: "password",
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ row }) => (
          <Close
            className={classes.deleteXIcon}
            onClick={() => handleOpenDeleteConfirm("teacher", row.original)}
          />
        ),
      },
    ],
    [classes.deleteXIcon]
  );

  return (
    <div>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <CustomTabs
            title=""
            headerColor="primary"
            tabs={[
              // {
              //   tabName: "All Users",
              //   tabContent: (
              //     <div className={classes.searchWrapper}>
              //       <ReactTable columns={columns} data={allUsers} />
              //     </div>
              //   ),
              // },
              {
                tabName: "Independent Readers",
                tabContent: (
                  <div className={classes.searchWrapper}>
                    <div className={classes.tableToolbar}>
                      <MuiButton
                        variant="contained"
                        startIcon={<Add />}
                        className={classes.systemButton}
                        onClick={handleOpenAddIndependentReader}
                      >
                        Add New
                      </MuiButton>
                    </div>

                    <ReactTable
                      columns={independentReaderColumns}
                      data={allIndependentReaders}
                    />
                  </div>
                ),
              },
              {
                tabName: "School Students",
                tabContent: (
                  <div className={classes.searchWrapper}>
                    <div className={classes.tableToolbar}>
                      <MuiButton
                        variant="contained"
                        startIcon={<Add />}
                        className={classes.systemButton}
                        onClick={handleOpenAddSchoolStudent}
                      >
                        Add New
                      </MuiButton>

                      <MuiButton
                        variant="contained"
                        className={classes.warningButton}
                        onClick={handleOpenBulkUploadSchoolStudents}
                      >
                        Bulk Upload CSV
                      </MuiButton>
                    </div>

                    <ReactTable
                      columns={schoolStudentColumns}
                      data={allSchoolStudents}
                    />
                  </div>
                ),
              },
              {
                tabName: "School Teachers",
                tabContent: (
                  <div className={classes.searchWrapper}>
                    <div className={classes.tableToolbar}>
                      <MuiButton
                        variant="contained"
                        startIcon={<Add />}
                        className={classes.systemButton}
                        onClick={handleOpenAddTeacher}
                      >
                        Add New
                      </MuiButton>
                    </div>

                    <ReactTable
                      columns={schoolTeacherColumns}
                      data={allSchoolTeachers}
                    />
                  </div>
                ),
              },
              {
      tabName: "Find Student",
      tabContent: <FindStudentTab />,
    },
        {
      tabName: "Support",
      tabContent: <SupportTab />,  // <-- Ajoute ceci
    },
            ]}
          />
        </GridItem>
      </GridContainer>

      <Dialog
        open={openMessage}
        onClose={onCloseMessage}
        fullWidth
        maxWidth="md"
        aria-labelledby="send-message-dialog-title"
      >
        <DialogTitle
          id="send-message-dialog-title"
          className={classes.dialogTitleRoot}
        >
          <div className={classes.dialogHeader}>
            <div className={classes.dialogHeaderLeft}>
              <Typography variant="h6" style={{ color: "#fff", fontWeight: 600 }}>
                Send Message
              </Typography>
            </div>

            <IconButton onClick={onCloseMessage} style={{ color: "#fff" }}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 700, marginBottom: 8 }}
          >
            SEND MESSAGE TO: {selectedStudent.first_name} {selectedStudent.last_name}
          </Typography>

          <Typography
            variant="body2"
            style={{ marginBottom: 16, color: "#666" }}
          >
            {selectedStudent.email}
          </Typography>

          <TextField
            label="Compose Message"
            placeholder="Type your message here..."
            multiline
            rows={6}
            variant="outlined"
            fullWidth
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            disabled={sendingMessage}
          />
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <MuiButton
            variant="contained"
            style={{ backgroundColor: "#F2B500", color: "#000" }}
            onClick={() => handleSendMessage("one")}
            disabled={sendingMessage}
          >
            {sendingMessage ? "Sending..." : "Send to This Student"}
          </MuiButton>

          <MuiButton
            variant="contained"
            style={{ backgroundColor: "#F2B500", color: "#000" }}
            onClick={() => handleSendMessage("all")}
            disabled={sendingMessage}
          >
            {sendingMessage ? "Sending..." : "Send to All Students"}
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openReport}
        onClose={onCloseReport}
        fullWidth
        maxWidth="xl"
        aria-labelledby="reading-report-dialog-title"
      >
        <DialogTitle
          id="reading-report-dialog-title"
          className={classes.dialogTitleRoot}
        >
          <div className={classes.dialogHeader}>
            <div className={classes.dialogHeaderLeft}>
              <Typography variant="h6" style={{ color: "#fff", fontWeight: 600 }}>
                Reading Report
                {selectedStudentForReport.first_name ||
                selectedStudentForReport.last_name
                  ? ` - ${selectedStudentForReport.first_name} ${selectedStudentForReport.last_name}`
                  : ""}
              </Typography>
            </div>
            <IconButton onClick={onCloseReport} style={{ color: "#fff" }}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ width: "100%", padding: "16px" }}>
            {loadingReport ? (
              <div
                style={{
                  height: "72vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  background: "#fff",
                }}
              >
                <Typography variant="h6" style={{ color: "#666", fontWeight: 600 }}>
                  Loading reading data...
                </Typography>
              </div>
            ) : (
              <div
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  overflow: "hidden",
                  background: "#fff",
                  height: "72vh",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ flexShrink: 0 }}>
                  <table
                    style={{
                      width: "100%",
                      tableLayout: "fixed",
                      borderCollapse: "collapse",
                    }}
                  >
                    <colgroup>
                      <col style={{ width: "30%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                    </colgroup>
                    <thead>
                      <tr style={{ backgroundColor: "#6B9D94" }}>
                        <th
                          className="px-4 py-4 text-left font-bold"
                          style={{ color: "#000", fontSize: "16px" }}
                        >
                          Book title
                        </th>
                        <th
                          className="px-4 py-4 text-center font-bold"
                          style={{ color: "#000", fontSize: "16px" }}
                        >
                          Completion
                        </th>
                        <th
                          className="px-4 py-4 text-center font-bold"
                          style={{ color: "#000", fontSize: "16px" }}
                        >
                          Minutes/Day
                        </th>
                        <th
                          className="px-4 py-4 text-center font-bold"
                          style={{ color: "#000", fontSize: "16px" }}
                        >
                          Words/minute
                        </th>
                        <th
                          className="px-4 py-4 text-center font-bold"
                          style={{ color: "#000", fontSize: "16px" }}
                        >
                          Comprehension
                        </th>
                        <th
                          className="px-4 py-4 text-center font-bold"
                          style={{ color: "#000", fontSize: "16px" }}
                        >
                          Overall Fluency
                        </th>
                      </tr>
                    </thead>
                  </table>
                </div>

                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      tableLayout: "fixed",
                      borderCollapse: "collapse",
                    }}
                  >
                    <colgroup>
                      <col style={{ width: "30%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                    </colgroup>
                    <tbody>
                      {selectedStudentForReport.books.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-12 text-center text-gray-500"
                          >
                            No reading data yet. Start reading a book to see your report.
                          </td>
                        </tr>
                      ) : (
                        selectedStudentForReport.books.map((book, index) => (
                          <tr
                            key={book.id}
                            className={`border-b border-gray-200 ${
                              index % 2 === 0 ? "bg-gray-50" : "bg-white"
                            }`}
                          >
                            <td className="px-4 py-3 text-left break-words">
                              {book.book_title}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {book.completion_percent.toFixed(1)}%
                            </td>
                            <td className="px-4 py-3 text-center">
                              {book.minutes_per_day.toFixed(1)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {book.wpm.toFixed(1)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {book.comprehension.toFixed(1)}%
                            </td>
                            <td
                              className="px-4 py-3 text-center font-semibold"
                              style={{ color: "#EF4444" }}
                            >
                              {book.fluency_score.toFixed(1)}%
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div
                  style={{
                    flexShrink: 0,
                    borderTop: "3px solid #94a3b8",
                    background: "#ffffff",
                    position: "relative",
                    zIndex: 20,
                    boxShadow: "0 -8px 22px rgba(0,0,0,0.16)",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      tableLayout: "fixed",
                      borderCollapse: "collapse",
                    }}
                  >
                    <colgroup>
                      <col style={{ width: "30%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                    </colgroup>
                    <tbody>
                      <tr style={{ backgroundColor: "#6B9D94" }}>
                        <th className="px-4 py-3 text-left font-semibold text-white"></th>
                        <th className="px-4 py-3 text-center font-semibold text-white"></th>
                        <th className="px-4 py-3 text-center font-semibold text-white"></th>
                        <th className="px-4 py-3 text-center font-semibold text-white"></th>
                        <th className="px-4 py-3 text-center font-semibold text-white"></th>
                        <th className="px-4 py-3 text-center font-semibold text-white"></th>
                      </tr>

                      <tr
                        style={{
                          background: "#f8fafc",
                          color: "#111827",
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        <th
                          className="px-4 py-4 text-left font-extrabold"
                          style={{ fontSize: "17px" }}
                        >
                          Totals book
                        </th>
                        <th
                          className="px-4 py-4 text-center font-extrabold"
                          style={{ fontSize: "17px" }}
                        >
                          Completion Average
                        </th>
                        <th
                          className="px-4 py-4 text-center font-extrabold"
                          style={{ fontSize: "17px" }}
                        >
                          Minutes/Day Average
                        </th>
                        <th
                          className="px-4 py-4 text-center font-extrabold"
                          style={{ fontSize: "17px" }}
                        >
                          Speed Average
                        </th>
                        <th
                          className="px-4 py-4 text-center font-extrabold"
                          style={{ fontSize: "17px" }}
                        >
                          Comp Average
                        </th>
                        <th
                          className="px-4 py-4 text-center font-extrabold"
                          style={{ fontSize: "17px" }}
                        >
                          Fluency Average
                        </th>
                      </tr>

                      <tr style={{ background: "#ffffff", color: "#111827" }}>
                        <th
                          className="px-4 py-5 text-left font-extrabold"
                          style={{ fontSize: "24px" }}
                        >
                          {selectedStudentForReport.overall?.total_books ?? 0}
                        </th>
                        <th
                          className="px-4 py-5 text-center font-extrabold"
                          style={{ fontSize: "24px", color: "#2563eb" }}
                        >
                          {(
                            selectedStudentForReport.overall?.avg_completion ?? 0
                          ).toFixed(1)}
                          %
                        </th>
                        <th
                          className="px-4 py-5 text-center font-extrabold"
                          style={{ fontSize: "24px", color: "#2563eb" }}
                        >
                          {(
                            selectedStudentForReport.overall?.avg_minutes_per_day ?? 0
                          ).toFixed(1)}
                        </th>
                        <th
                          className="px-4 py-5 text-center font-extrabold"
                          style={{ fontSize: "24px", color: "#2563eb" }}
                        >
                          {(selectedStudentForReport.overall?.avg_wpm ?? 0).toFixed(1)}
                        </th>
                        <th
                          className="px-4 py-5 text-center font-extrabold"
                          style={{ fontSize: "24px", color: "#2563eb" }}
                        >
                          {(
                            selectedStudentForReport.overall?.avg_comprehension ?? 0
                          ).toFixed(1)}
                          %
                        </th>
                        <th
                          className="px-4 py-5 text-center font-extrabold"
                          style={{ fontSize: "24px", color: "#ef4444" }}
                        >
                          {(
                            selectedStudentForReport.overall?.avg_fluency ?? 0
                          ).toFixed(1)}
                          %
                        </th>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <MuiButton
            variant="contained"
            style={{ backgroundColor: "#6B9D94", color: "#fff" }}
            onClick={onCloseReport}
          >
            Close
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddIndependentReader}
        onClose={handleCloseAddIndependentReader}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className={classes.dialogTitleRoot}>
          <div className={classes.dialogHeader}>
            <div className={classes.dialogHeaderLeft}>
              <Typography variant="h6" style={{ color: "#fff", fontWeight: 600 }}>
                Add New Independent Reader
              </Typography>
            </div>

            <IconButton
              onClick={handleCloseAddIndependentReader}
              style={{ color: "#fff" }}
            >
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <TextField
            label="First Name"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            value={independentReaderForm.firstName}
            onChange={(e) =>
              setIndependentReaderForm((prev) => ({
                ...prev,
                firstName: e.target.value,
              }))
            }
          />

          <TextField
            label="Last Name"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            value={independentReaderForm.lastName}
            onChange={(e) =>
              setIndependentReaderForm((prev) => ({
                ...prev,
                lastName: e.target.value,
              }))
            }
          />

          <TextField
            label="Email Address"
            variant="outlined"
            fullWidth
            value={independentReaderForm.email}
            onChange={(e) =>
              setIndependentReaderForm((prev) => ({
                ...prev,
                email: e.target.value,
              }))
            }
          />
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <MuiButton
            variant="contained"
            onClick={handleCreateIndependentReader}
            className={classes.warningButton}
            disabled={creatingIndependentReader}
          >
            {creatingIndependentReader ? "Saving..." : "Save"}
          </MuiButton>

          <MuiButton
            variant="contained"
            onClick={handleCloseAddIndependentReader}
            className={classes.systemButton}
            disabled={creatingIndependentReader}
          >
            Cancel
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddSchoolStudent}
        onClose={handleCloseAddSchoolStudent}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className={classes.dialogTitleRoot}>
          <div className={classes.dialogHeader}>
            <div className={classes.dialogHeaderLeft}>
              <Typography variant="h6" style={{ color: "#fff", fontWeight: 600 }}>
                Add New School Student
              </Typography>
            </div>

            <IconButton
              onClick={handleCloseAddSchoolStudent}
              style={{ color: "#fff" }}
            >
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <TextField
            label="School ID"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            value={schoolStudentForm.school_id}
            onChange={(e) =>
              setSchoolStudentForm((prev) => ({
                ...prev,
                school_id: normalizeSchoolIdValue(e.target.value),
                teacher_id: "",
              }))
            }
            helperText="When you type School ID, teachers are loaded automatically."
          />

          <TextField
            select
            label="Teacher"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            value={schoolStudentForm.teacher_id}
            onChange={(e) =>
              setSchoolStudentForm((prev) => ({
                ...prev,
                teacher_id: e.target.value,
              }))
            }
            disabled={
              !schoolStudentForm.school_id ||
              loadingSchoolStudentTeacherOptions
            }
            helperText={
              loadingSchoolStudentTeacherOptions ? "Loading teachers..." : ""
            }
          >
            <MenuItem value="">
              <em>Select teacher</em>
            </MenuItem>

            {schoolStudentTeacherOptions.map((teacher) => (
              <MenuItem
                key={teacher.teacher_id}
                value={String(teacher.teacher_id)}
              >
                {teacher.first_name} {teacher.last_name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="First Name"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            value={schoolStudentForm.firstName}
            onChange={(e) =>
              setSchoolStudentForm((prev) => ({
                ...prev,
                firstName: e.target.value,
              }))
            }
          />

          <TextField
            label="Last Name"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            value={schoolStudentForm.lastName}
            onChange={(e) =>
              setSchoolStudentForm((prev) => ({
                ...prev,
                lastName: e.target.value,
              }))
            }
          />

          <TextField
            label="Email Address"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            value={schoolStudentForm.email}
            onChange={(e) =>
              setSchoolStudentForm((prev) => ({
                ...prev,
                email: e.target.value,
                username: e.target.value,
              }))
            }
            helperText="Username will be set to the email address"
          />

          <TextField
            label="Password (optional)"
            variant="outlined"
            fullWidth
            value={schoolStudentForm.password}
            onChange={(e) =>
              setSchoolStudentForm((prev) => ({
                ...prev,
                password: e.target.value,
              }))
            }
            helperText="If empty, first name will be used as password"
          />
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <MuiButton
            variant="contained"
            onClick={handleCreateSchoolStudent}
            className={classes.warningButton}
            disabled={creatingSchoolStudent}
          >
            {creatingSchoolStudent ? "Saving..." : "Save"}
          </MuiButton>

          <MuiButton
            variant="contained"
            onClick={handleCloseAddSchoolStudent}
            className={classes.systemButton}
            disabled={creatingSchoolStudent}
          >
            Cancel
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openBulkUploadSchoolStudents}
        onClose={handleCloseBulkUploadSchoolStudents}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className={classes.dialogTitleRoot}>
          <div className={classes.dialogHeader}>
            <div className={classes.dialogHeaderLeft}>
              <Typography variant="h6" style={{ color: "#fff", fontWeight: 600 }}>
                Bulk Upload School Students
              </Typography>
            </div>

            <IconButton
              onClick={handleCloseBulkUploadSchoolStudents}
              style={{ color: "#fff" }}
            >
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <TextField
            label="School ID"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            value={bulkSchoolStudentForm.school_id}
            onChange={(e) =>
              setBulkSchoolStudentForm((prev) => ({
                ...prev,
                school_id: normalizeSchoolIdValue(e.target.value),
                teacher_id: "",
              }))
            }
            helperText="Teachers are loaded automatically from the School ID."
          />

          <TextField
            select
            label="Teacher"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            value={bulkSchoolStudentForm.teacher_id}
            onChange={(e) =>
              setBulkSchoolStudentForm((prev) => ({
                ...prev,
                teacher_id: e.target.value,
              }))
            }
            disabled={
              !bulkSchoolStudentForm.school_id ||
              loadingBulkSchoolTeacherOptions
            }
            helperText={
              loadingBulkSchoolTeacherOptions ? "Loading teachers..." : ""
            }
          >
            <MenuItem value="">
              <em>Select teacher</em>
            </MenuItem>

            {bulkSchoolTeacherOptions.map((teacher) => (
              <MenuItem
                key={teacher.teacher_id}
                value={String(teacher.teacher_id)}
              >
                {teacher.first_name} {teacher.last_name}
              </MenuItem>
            ))}
          </TextField>

          <Typography
            variant="body2"
            style={{ marginBottom: 12, color: "#666" }}
          >
            CSV must be semicolon separated with columns:
            <strong> First Name; Last Name; Email Address; School username; School password, Group Name (optional)</strong>
          </Typography>

          <Typography
            variant="body2"
            style={{ marginBottom: 16, color: "#666" }}
          >
           If School username is empty, email is used. If School password is empty, first name is used.
          </Typography>

          <TextField
            type="file"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            inputProps={{ accept: ".csv,text/csv" }}
            onChange={handleBulkSchoolStudentFileChange}
            helperText={
              bulkSchoolStudentForm.file
                ? `Selected file: ${bulkSchoolStudentForm.file.name}`
                : "Choose a CSV file"
            }
          />
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <MuiButton
            variant="contained"
            onClick={handleBulkUploadSchoolStudents}
            className={classes.warningButton}
            disabled={bulkUploadingSchoolStudents}
          >
            {bulkUploadingSchoolStudents ? "Uploading..." : "Upload"}
          </MuiButton>

          <MuiButton
            variant="contained"
            onClick={handleCloseBulkUploadSchoolStudents}
            className={classes.systemButton}
            disabled={bulkUploadingSchoolStudents}
          >
            Cancel
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddTeacher}
        onClose={handleCloseAddTeacher}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className={classes.dialogTitleRoot}>
          <div className={classes.dialogHeader}>
            <div className={classes.dialogHeaderLeft}>
              <Typography variant="h6" style={{ color: "#fff", fontWeight: 600 }}>
                Add New Teacher
              </Typography>
            </div>

            <IconButton onClick={handleCloseAddTeacher} style={{ color: "#fff" }}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <TextField
            label="School ID"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            value={teacherForm.school_id}
            onChange={(e) =>
              setTeacherForm((prev) => ({
                ...prev,
                school_id: normalizeSchoolIdValue(e.target.value),
              }))
            }
          />

          <TextField
            label="First Name"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            value={teacherForm.firstName}
            onChange={(e) =>
              setTeacherForm((prev) => ({
                ...prev,
                firstName: e.target.value,
              }))
            }
          />

          <TextField
            label="Last Name"
            variant="outlined"
            fullWidth
            className={classes.inputSpacing}
            value={teacherForm.lastName}
            onChange={(e) =>
              setTeacherForm((prev) => ({
                ...prev,
                lastName: e.target.value,
              }))
            }
          />

          <TextField
            label="Email Address"
            variant="outlined"
            fullWidth
            value={teacherForm.email}
            onChange={(e) =>
              setTeacherForm((prev) => ({
                ...prev,
                email: e.target.value,
              }))
            }
          />
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <MuiButton
            variant="contained"
            onClick={handleCreateTeacher}
            className={classes.warningButton}
            disabled={creatingTeacher}
          >
            {creatingTeacher ? "Saving..." : "Save"}
          </MuiButton>

          <MuiButton
            variant="contained"
            onClick={handleCloseAddTeacher}
            className={classes.systemButton}
            disabled={creatingTeacher}
          >
            Cancel
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className={classes.dialogTitleRoot}>
          <div className={classes.dialogHeader}>
            <div className={classes.dialogHeaderLeft}>
              <Typography variant="h6" style={{ color: "#fff", fontWeight: 600 }}>
                Confirm Delete
              </Typography>
            </div>

            <IconButton
              onClick={handleCloseDeleteConfirm}
              style={{ color: "#fff" }}
            >
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <Typography
            variant="body1"
            style={{ fontWeight: 600, marginBottom: "12px" }}
          >
            Are you sure you want to delete {deleteTarget.label}?
          </Typography>

          <Typography variant="body2" style={{ color: "#666" }}>
            This action will remove this{" "}
            {deleteTarget.type === "teacher" ? "teacher" : "student"} from the
            system. Please confirm before continuing.
          </Typography>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <MuiButton
            variant="contained"
            style={{ backgroundColor: "#d32f2f", color: "#fff" }}
            onClick={handleConfirmDelete}
            disabled={deletingItem}
          >
            {deletingItem ? "Deleting..." : "Delete"}
          </MuiButton>

          <MuiButton
            variant="contained"
            className={classes.systemButton}
            onClick={handleCloseDeleteConfirm}
            disabled={deletingItem}
          >
            Cancel
          </MuiButton>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </div>
  );
}